import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlayIcon, PauseIcon, TrashIcon, ChevronRightIcon, UserIcon, ClockIcon, CalendarIcon, SearchIcon, FilterIcon } from 'lucide-react';
import { useRecording } from '../../contexts/RecordingContext';
export const RecordingsList: React.FC = () => {
  const {
    recordings,
    deleteRecording
  } = useRecording();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  // Filter recordings based on search term and filter
  const filteredRecordings = recordings.filter(recording => {
    // Apply filter
    if (filter === 'with-customer' && !recording.customerId) return false;
    if (filter === 'without-customer' && recording.customerId) return false;
    // Apply search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return recording.title.toLowerCase().includes(term);
    }
    return true;
  });
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };
  const handleViewRecording = (id: string) => {
    navigate(`/recordings/${id}`);
  };
  return <div className="space-y-6" data-id="element-1466">
      <div className="flex justify-between items-center" data-id="element-1467">
        <h1 className="text-2xl font-bold text-gray-800" data-id="element-1468">
          Conversation Recordings
        </h1>
      </div>
      {/* Search and filters */}
      <div className="flex space-x-4" data-id="element-1469">
        <div className="relative flex-1" data-id="element-1470">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1471">
            <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-1472" />
          </div>
          <input type="text" placeholder="Search recordings..." className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-1473" />
        </div>
        <select className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={filter} onChange={e => setFilter(e.target.value)} data-id="element-1474">
          <option value="all" data-id="element-1475">All Recordings</option>
          <option value="with-customer" data-id="element-1476">With Customer</option>
          <option value="without-customer" data-id="element-1477">Without Customer</option>
        </select>
        <button className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-1478">
          <FilterIcon className="h-4 w-4 mr-2" data-id="element-1479" />
          More Filters
        </button>
      </div>
      {/* Recordings list */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden" data-id="element-1480">
        {filteredRecordings.length === 0 ? <div className="p-8 text-center" data-id="element-1481">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1482">
              <ClockIcon className="h-8 w-8 text-gray-400" data-id="element-1483" />
            </div>
            <h3 className="text-lg font-medium text-gray-800 mb-2" data-id="element-1484">
              No recordings found
            </h3>
            <p className="text-gray-500 mb-4" data-id="element-1485">
              Try adjusting your search or filters
            </p>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" onClick={() => {
          setSearchTerm('');
          setFilter('all');
        }} data-id="element-1486">
              Reset Filters
            </button>
          </div> : <div className="overflow-x-auto" data-id="element-1487">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-1488">
              <thead className="bg-gray-50" data-id="element-1489">
                <tr data-id="element-1490">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1491">
                    Recording
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1492">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1493">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1494">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1495">
                    Analysis
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1496">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-1497">
                {filteredRecordings.map(recording => <tr key={recording.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleViewRecording(recording.id)} data-id="element-1498">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1499">
                      <div className="flex items-center" data-id="element-1500">
                        <button className="p-2 bg-blue-100 text-blue-600 rounded-full mr-3" data-id="element-1501">
                          <PlayIcon className="h-4 w-4" data-id="element-1502" />
                        </button>
                        <div data-id="element-1503">
                          <div className="text-sm font-medium text-gray-900" data-id="element-1504">
                            {recording.title}
                          </div>
                          {recording.transcription && <div className="text-sm text-gray-500 truncate max-w-xs" data-id="element-1505">
                              {recording.transcription.substring(0, 50)}...
                            </div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1506">
                      {recording.customerId ? <div className="flex items-center" data-id="element-1507">
                          <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600" data-id="element-1508">
                            <UserIcon className="h-4 w-4" data-id="element-1509" />
                          </div>
                          <div className="ml-3" data-id="element-1510">
                            <div className="text-sm font-medium text-gray-900" data-id="element-1511">
                              {recording.customerId === 'cust-1' ? 'Jennifer Williams' : recording.customerId === 'cust-2' ? 'Robert Chen' : recording.customerId === 'cust-3' ? 'Michael Torres' : 'Emily Wilson'}
                            </div>
                          </div>
                        </div> : <span className="text-sm text-gray-500" data-id="element-1512">
                          Not assigned
                        </span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1513">
                      <div className="flex items-center" data-id="element-1514">
                        <ClockIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-1515" />
                        <span className="text-sm text-gray-900" data-id="element-1516">
                          {formatDuration(recording.duration)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1517">
                      <div className="flex items-center" data-id="element-1518">
                        <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-1519" />
                        <span className="text-sm text-gray-900" data-id="element-1520">
                          {formatDate(recording.date)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1521">
                      {recording.analysis ? <div className="flex flex-col" data-id="element-1522">
                          <span className="text-xs font-medium text-gray-900" data-id="element-1523">
                            {recording.analysis.customerSummary.substring(0, 30)}
                            ...
                          </span>
                          <div className="flex space-x-1 mt-1" data-id="element-1524">
                            {recording.analysis.destinations.slice(0, 2).map((dest, i) => <span key={i} className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full" data-id="element-1525">
                                  {dest}
                                </span>)}
                            {recording.analysis.destinations.length > 2 && <span className="px-2 py-0.5 bg-gray-100 text-gray-800 text-xs rounded-full" data-id="element-1526">
                                +{recording.analysis.destinations.length - 2}
                              </span>}
                          </div>
                        </div> : <span className="text-sm text-gray-500" data-id="element-1527">
                          Processing...
                        </span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1528">
                      <div className="flex items-center justify-end space-x-2" data-id="element-1529">
                        <button className="text-red-600 hover:text-red-900" onClick={e => {
                    e.stopPropagation();
                    deleteRecording(recording.id);
                  }} data-id="element-1530">
                          <TrashIcon className="h-4 w-4" data-id="element-1531" />
                        </button>
                        <button className="text-blue-600 hover:text-blue-900" data-id="element-1532">
                          <ChevronRightIcon className="h-5 w-5" data-id="element-1533" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>
    </div>;
};